import styled from "styled-components";

import type { AreaGraphProps } from "@similarweb/pro-logic-components";
import { AreaGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import { EmptyState } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type {
    GranularityType,
    ValueType,
} from "app/pages/digital-marketing/monitor-and-protect/shared/components/competitors-chart/types";

import { translate } from "app/filters/i18nFilter";
import { NumberFormatter } from "app/pages/digital-marketing/monitor-and-protect/services/number-formatter";
import { competitorsTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/competitors";
import { useTranslation } from "pro-features/components/WithTranslation";

import { getLegendHeaders } from "./chart-assets";
import { useCompetitorsChartConfig } from "./use-competitors-chart-config";

const FlexFixer = styled(Flex)`
    padding: 24px 24px 24px 24px;
    > *:first-child {
        flex: 1;
        width: 100%;
    }

    > div > div:last-child {
        padding: 0;
    }
`;

interface ICompetitorsChartProps {
    granularity: GranularityType;
    valueType: ValueType;
    chartData: AreaGraphProps<unknown>["data"];
    onSelectionChange: (selection: Record<string, boolean>) => void;
}

export const CompetitorsChart = ({ granularity, valueType, chartData, onSelectionChange }: ICompetitorsChartProps) => {
    const chartConfig = useCompetitorsChartConfig(granularity, valueType, chartData);
    const translateMnp = useTranslation();

    if (!chartData || chartData.length === 0 || chartData.every((series) => !series.data || series.data.length === 0)) {
        return (
            <Flex
                paddingY="44px"
                justifyContent="center"
                alignItems="center"
                width="100%"
                height="100%"
            >
                <EmptyState
                    title={translate("global.graph.no-data")}
                    subtitle="Please try adjusting your filters"
                />
            </Flex>
        );
    }

    return (
        <FlexFixer width="100%">
            <AreaGraph
                graphConfig={chartConfig}
                isLegendSearch
                legendSearchPlaceholder={translateMnp(
                    "monitor_and_protect.competitors.chart.legend.search_placeholder",
                )}
                disabledLegendItemTooltip={translate("monitorandprotect.feature.competitors.chart.limit")}
                data={chartData}
                legendGraphGap={24}
                maxLegendItemsSelected={10}
                isControlledGraph={true}
                legendPosition={LEGEND_POSITIONS.RIGHT}
                shouldPresentValueAtLegend={true}
                legendsHeight="295px"
                legendsWithValueMaxWidth={260}
                legendsMinWidth={260}
                legendToggleHandlerCallback={(visibleSeries) => {
                    const newSelection: Record<string, boolean> = {};
                    chartData.forEach((item) => {
                        newSelection[item.id] = visibleSeries.some((s: any) => s.id === item.id);
                    });
                    onSelectionChange(newSelection);
                }}
                legendHeaders={getLegendHeaders(translateMnp)}
                isToggleAllEnabled
                legendValueFilter={(v) => NumberFormatter.toAbbr(v, 0)}
                componentNameForTracking={"competitor"}
                customLegendTrackingHandler={(itemName, action) => {
                    const isToggleAll = itemName === "toggleAll";
                    competitorsTracking.onChartFilter({
                        action: isToggleAll ? itemName : action,
                        replacements: { filter: "competitor" + (isToggleAll ? action : itemName) },
                    });
                }}
            />
        </FlexFixer>
    );
};
